import React, { useEffect, useRef, useState } from 'react'
import {
  Check,
  ChevronDown,
  ChevronRight,
  LogOut,
  Monitor,
  Moon,
  Palette,
  PenLine,
  Settings,
  Sun,
  UserRound
} from 'lucide-react'
import { Avatar } from '@astryxdesign/core/Avatar'
import { DEFAULT_AVATAR_IMAGE } from '../lib/defaultAvatarImage'
import { Divider } from '@astryxdesign/core/Divider'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { List, ListItem } from '@astryxdesign/core/List'
import { Popover } from '@astryxdesign/core/Popover'
import { Section } from '@astryxdesign/core/Section'
import { Text } from '@astryxdesign/core/Text'
import PillButton from './PillButton'
import { authClient } from '../lib/auth-client'
import { startWriting } from '../lib/startWriting'
import { useIsMobile } from '../lib/useMediaQuery'

export interface LandingHeaderProps {
  onNavClick?: (link: string) => void
  onLogIn?: () => void
  onSignUp?: () => void
  showNavLinks?: boolean
  showAppButton?: boolean
  userName?: string
  avatarUrl?: string | null
  isAppHeader?: boolean
  isAccountLoading?: boolean
  /** Overrides what "Write" does. Without it the button falls back to {@link startWriting}. */
  onWrite?: () => void
  onOpenSettings?: (section: 'account' | 'profile') => void
  currentTheme?: 'light' | 'dark' | 'system'
  onThemeChange?: (theme: 'light' | 'dark' | 'system') => void
}

const NAV_LINKS = ['Stories', 'Pricing']

export interface AppAccountButtonProps {
  displayName: string
  image?: string | null
  placement?: 'above' | 'below'
  onOpenSettings?: (section: 'account' | 'profile') => void
  currentTheme?: 'light' | 'dark' | 'system'
  onThemeChange?: (theme: 'light' | 'dark' | 'system') => void
  /** Drops the visible name and shows the avatar alone; the name stays the accessible name. */
  isCompact?: boolean
}

export function AppAccountButton({
  displayName,
  image,
  placement = 'below',
  onOpenSettings,
  currentTheme,
  onThemeChange,
  isCompact = false
}: AppAccountButtonProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system')

  useEffect(() => {
    if (currentTheme) return
    window.api.settings
      .get()
      .then((settings) => setTheme(settings.theme))
      .catch(() => undefined)
  }, [currentTheme])

  const selectedTheme = currentTheme ?? theme

  const selectTheme = async (nextTheme: 'light' | 'dark' | 'system'): Promise<void> => {
    const settings = await window.api.settings.get()
    await window.api.settings.save({ ...settings, theme: nextTheme })
    const resolvedTheme =
      nextTheme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : nextTheme
    document.documentElement.setAttribute('data-theme', resolvedTheme)
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark')
    document.documentElement.style.colorScheme = resolvedTheme
    setTheme(nextTheme)
    onThemeChange?.(nextTheme)
    setIsOpen(false)
  }

  // Same rule as the workspace's account menu: Profile goes to the writer's public profile
  // page, the one readers see. Editing stays under Account settings. Falls back to the
  // settings section only when no profile resolves, so the item never silently does nothing.
  const openOwnProfilePage = async (): Promise<void> => {
    setIsOpen(false)
    try {
      const profile = await window.api.profiles.getActive()
      if (profile?.id) {
        window.location.href = `/profiles/${encodeURIComponent(profile.id)}`
        return
      }
    } catch {
      // fall through to the settings section
    }
    openSettings('profile')
  }

  const openSettings = (section: 'account' | 'profile'): void => {
    setIsOpen(false)
    // Every app view supplies onOpenSettings; the fallback is for a host that cannot open the
    // settings dialog itself, and sends the writer somewhere they can.
    if (onOpenSettings) onOpenSettings(section)
    else window.location.href = '/my-stories'
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setIsThemeMenuOpen(false)
      }}
      placement={placement}
      alignment={placement === 'above' ? 'start' : 'end'}
      width={220}
      label="Account menu"
      className="account-menu"
      content={
        <VStack gap={2}>
          <List density="compact">
            <ListItem
              label="Account settings"
              startContent={<Settings size={16} />}
              onClick={() => openSettings('account')}
            />
            <ListItem
              label="Profile"
              startContent={<UserRound size={16} />}
              onClick={() => void openOwnProfilePage()}
            />
            <ListItem
              label={`Theme (${selectedTheme.charAt(0).toUpperCase() + selectedTheme.slice(1)})`}
              startContent={<Palette size={16} />}
              endContent={isThemeMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              onClick={() => setIsThemeMenuOpen((open) => !open)}
            />
            {isThemeMenuOpen && (
              <>
                <ListItem
                  label="System"
                  startContent={<Monitor size={16} />}
                  endContent={selectedTheme === 'system' ? <Check size={16} /> : null}
                  isSelected={selectedTheme === 'system'}
                  className="account-theme-option"
                  onClick={() => void selectTheme('system')}
                />
                <ListItem
                  label="Light"
                  startContent={<Sun size={16} />}
                  endContent={selectedTheme === 'light' ? <Check size={16} /> : null}
                  isSelected={selectedTheme === 'light'}
                  className="account-theme-option"
                  onClick={() => void selectTheme('light')}
                />
                <ListItem
                  label="Dark"
                  startContent={<Moon size={16} />}
                  endContent={selectedTheme === 'dark' ? <Check size={16} /> : null}
                  isSelected={selectedTheme === 'dark'}
                  className="account-theme-option"
                  onClick={() => void selectTheme('dark')}
                />
              </>
            )}
          </List>
          <Divider />
          <List density="compact">
            <ListItem
              label="Sign out"
              startContent={<LogOut size={16} />}
              onClick={() => void authClient.signOut().then(() => window.location.replace('/'))}
            />
          </List>
        </VStack>
      }
    >
      <PillButton
        variant="ghost"
        label={displayName}
        isIconOnly={isCompact}
        icon={<Avatar name={displayName} src={image ?? DEFAULT_AVATAR_IMAGE} size='xsm' />}
        className="account-menu-trigger"
      />
    </Popover>
  );
}

export function Wordmark({ size = 'md' }: { size?: 'md' | 'lg' }): React.JSX.Element {
  return (
    <a href="/home" style={{ textDecoration: 'none' }} aria-label="Nine Nights home">
      <HStack gap={0} align="center" className={`landing-wordmark landing-wordmark-${size}`}>
        <Text as="span" className="landing-wordmark-accent">
          9
        </Text>
        <Text as="span" className="landing-wordmark-rest">
          nights
        </Text>
      </HStack>
    </a>
  )
}

export default function LandingHeader({
  onNavClick,
  onLogIn,
  onSignUp,
  showNavLinks = true,
  showAppButton = true,
  userName,
  avatarUrl,
  isAppHeader = false,
  isAccountLoading = false,
  onWrite,
  onOpenSettings,
  currentTheme,
  onThemeChange
}: LandingHeaderProps): React.JSX.Element {
  const [isStartingWrite, setIsStartingWrite] = useState(false)
  const isMobile = useIsMobile()
  const writeInFlight = useRef(false)
  const { data: session } = authClient.useSession()
  const loggedInUser = session?.user
  const displayName =
    userName || loggedInUser?.name?.trim() || loggedInUser?.email?.split('@')[0] || null

  const handleLogIn = (): void => {
    if (onLogIn) {
      onLogIn()
    } else {
      window.location.href = '/'
    }
  }

  const handleSignUp = (): void => {
    if (onSignUp) {
      onSignUp()
    } else {
      window.location.href = '/'
    }
  }

  const handleGoToApp = (): void => {
    window.location.href = '/home'
  }

  const handleWrite = (): void => {
    if (onWrite) {
      onWrite()
      return
    }
    // Guarded by a ref, not the state: startWriting can create a story, and a double-click lands
    // in one tick, before React re-renders — so a state guard would still be `false` on the
    // second click and the writer would end up with two.
    if (writeInFlight.current) return
    writeInFlight.current = true
    setIsStartingWrite(true)
    startWriting().catch((reason) => {
      // The header has nowhere to show this, but re-enabling the button lets the writer retry
      // instead of staring at a control that has silently stopped responding.
      console.error('Failed to start writing:', reason)
      writeInFlight.current = false
      setIsStartingWrite(false)
    })
  }

  return (
    <Section
      variant="transparent"
      padding={0}
      className={`landing-nav${isAppHeader ? ' app-header' : ''}`}
    >
      <HStack
        justify="between"
        align="center"
        paddingInline={6}
        paddingBlock={3}
        className="landing-nav-inner"
      >
        <Wordmark />
        {showNavLinks && (
          <HStack gap={1} align="center" className="landing-nav-links">
            {NAV_LINKS.map((link) => (
              <PillButton
                key={link}
                variant="ghost"
                size="sm"
                label={link}
                onClick={() => onNavClick?.(link)}
              />
            ))}
          </HStack>
        )}
        <HStack gap={3} align="center">
          {isAccountLoading ? null : displayName ? (
            <>
              {isAppHeader && (
                <PillButton
                  variant="primary"
                  label="Write"
                  isIconOnly={isMobile}
                  icon={<PenLine size={16} />}
                  className="landing-btn-gold app-header-write"
                  isDisabled={isStartingWrite}
                  onClick={handleWrite}
                />
              )}
              {isAppHeader ? (
                <AppAccountButton
                  displayName={displayName}
                  image={avatarUrl}
                  isCompact={isMobile}
                  onOpenSettings={onOpenSettings}
                  currentTheme={currentTheme}
                  onThemeChange={onThemeChange}
                />
              ) : (
                <PillButton
                  variant="ghost"
                  label={displayName}
                  icon={<UserRound size={16} />}
                  className="landing-nav-login"
                  onClick={handleGoToApp}
                />
              )}
              {showAppButton && (
                <PillButton
                  variant="primary"
                  label="Go to App"
                  className="landing-btn-gold"
                  onClick={handleGoToApp}
                />
              )}
            </>
          ) : (
            <>
              <PillButton
                variant="ghost"
                label="Log in"
                className="landing-nav-login"
                onClick={handleLogIn}
              />
              <PillButton
                variant="primary"
                label="Sign up"
                className="landing-btn-gold"
                onClick={handleSignUp}
              />
            </>
          )}
        </HStack>
      </HStack>
    </Section>
  )
}
